import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import { Grid } from '@mui/material';

import { mutateRolesByName } from '@modules/ContactManager';
import { Modal } from '@shared/components/modals/Modal';
import { SERVICES, USER_SERVICE } from '@shared/constants/apiConfig';
import { IRoleUser } from '@shared/interfaces';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { sortUsers } from '..';
import { IUser } from '../../Users/interfaces';
import { IGroupOfUsers } from '../interfaces';
import { requestUpdateRoleAssignment } from '../queries';
import { GroupsOfUsers } from './components/GroupOfUsers/GroupsOfUsers';
import { RoleModalLayout } from './components/RoleModalLayout';
import { UsersFinalSet } from './UsersFinalSet';
import { AddUsers } from './components/AddUsers';
import { ExcludeUsers } from './components/ExcludeUsers';
import { applyCheckboxes, collectAllSelectedUsers, filterDuplicate } from './utils';
import { RoleAssignmentRequest } from './models/RoleAssignmentModel';
import { ILoggeRoundRobindUser, IRoleAssignmentSelection, RoleAssignmentSelection } from './models/roleAssignmentSelectionModel';
const TITLES = 'Titles';

interface IRoleModalProps {
  roleModalPosition: number;
  users?: IRoleUser[];
  groupOfUsers?: IGroupOfUsers;
  allUsers?: IUser[];
  roleName: string;
  roleId: string;
  handleClose(): void;
}

export const RoleModal = ({
  allUsers = [],
  users = [],
  handleClose,
  roleModalPosition,
  groupOfUsers,
  roleId,
  roleName,
}: IRoleModalProps) => {
  const shouldFetchRoundRobinLoggedUsers = roleName == TITLES;
  const checkboxesEndpoint = roleId && `/${SERVICES.USER}/roleAssignment/roleAssignmentSelections/${roleId}`;
  const { data: loggedRoundRobinUsers, isLoading: isRoundRobinLoading } = useSWR<ILoggeRoundRobindUser[]>(
    shouldFetchRoundRobinLoggedUsers ? `${USER_SERVICE.LOGGED_IN_ROUND_ROBIN}` : null
  );
  const { data: roleAssignmentSelections, mutate } = useSWR<IRoleAssignmentSelection>(checkboxesEndpoint);
  const [selectionModel, setSelectionModel] = useState<IRoleAssignmentSelection>(new RoleAssignmentSelection());
  const [selectedUsers, setSelectedUsers] = useState<IRoleUser[]>([]);
  const [includedUsers, setIncludedUsers] = useState<IRoleUser[]>([]);
  const [excludedUsers, setExcludedUsers] = useState<IRoleUser[]>([]);
  const [finalSetOfUsers, setFinalSetOfUsers] = useState<IRoleUser[]>([]);

  useEffect(() => {
    setSelectionModel(new RoleAssignmentSelection(applyCheckboxes(groupOfUsers, roleAssignmentSelections)));
  }, [roleAssignmentSelections]);

  const addUsersToFinalSet = (users: IRoleUser[]) => {
    setFinalSetOfUsers(sortUsers(filterDuplicate(users)));
  };

  useEffect(() => {
    setSelectedUsers(collectAllSelectedUsers(selectionModel));
  }, [selectionModel]);

  useEffect(() => {
    const included: IRoleUser[] = [];
    const excluded: IRoleUser[] = [];

    users?.forEach((user) => {
      if (user.isIncluded) {
        included.push(user);
      } else if (user.isExcluded) {
        excluded.push(user);
      }
    });

    setIncludedUsers(included);
    setExcludedUsers(excluded);
  }, [users]);

  useEffect(() => {
    const allUsersMapped = allUsers.map((user) => ({
      userId: user.id,
      userName: `${user.firstName} ${user.lastName}`,
      assignedFrom: 'All Users',
      isExcluded: false,
      isIncluded: false,
    }));

    const includedUsersSet = new Set(includedUsers.map((user) => user.userId));
    const excludedUsersSet = new Set(excludedUsers.map((user) => user.userId));

    const restUsers = selectionModel?.allUsers ? allUsersMapped : selectedUsers;

    addUsersToFinalSet(
      [...restUsers, ...includedUsers, ...excludedUsers].map((user) => {
        let assignedFrom = user.assignedFrom || '';
        let isIncluded = false,
          isExcluded = false;

        if (includedUsersSet.has(user.userId)) {
          isIncluded = true;
          assignedFrom = assignedFrom?.includes('Individual') ? assignedFrom : `Individual Included, ${assignedFrom}`;
        } else if (excludedUsersSet.has(user.userId)) {
          isExcluded = true;
          assignedFrom = assignedFrom?.includes('Individual') ? assignedFrom : `Individual Excluded, ${assignedFrom}`;
        }

        return {
          ...user,
          isIncluded,
          isExcluded,
          assignedFrom,
        };
      }),
    );
  }, [includedUsers, excludedUsers, selectedUsers]);

  const onSave = useCallback(() => {
    const request = new RoleAssignmentRequest(selectionModel, [...includedUsers, ...excludedUsers]);

    requestUpdateRoleAssignment(roleId, request)
      .then(handleClose)
      .then(() => globalMutate(`${USER_SERVICE.EDIT_USERS}/${roleId}`))
      .then(() => globalMutate(`${USER_SERVICE.ROLES}`))
      .then(() => mutateRolesByName())
      .then(() => mutate({ ...selectionModel }));
  }, [selectionModel, roleId, mutateRolesByName, includedUsers, excludedUsers]);

  const onUpdateAssignedUsers = useCallback(
    (
      user: IRoleUser,
      exclude = false,
      loggedRoundRobinUsers?: ILoggeRoundRobindUser[],
      shouldFetchRoundRobinLoggedUsers?: boolean,
      isRoundRobinLoading?: boolean
    ) => {
      if (
        shouldFetchRoundRobinLoggedUsers &&
        !isRoundRobinLoading &&
        user.isExcluded &&
        loggedRoundRobinUsers?.find((loggedUser) => loggedUser.userId === user.userId)
      ) {
        const message = `You can't exclude logged Round Robin user ${user.userName}`;
        Notistack.generalError(message);
        return;
      }
      setIncludedUsers((prevIncludedUsers) => {
        const filteredData = prevIncludedUsers.filter((prevIncludedUser) => prevIncludedUser.userId !== user.userId);

        if (exclude) {
          return filteredData;
        }

        return [
          ...filteredData,
          user
        ];
      });

      setExcludedUsers(prevExcludedUsers => {
        const filteredData = prevExcludedUsers.filter(prevExcludedUser => prevExcludedUser.userId !== user.userId);

        if (!exclude) {
          return filteredData;
        }

        return [
          ...filteredData,
          user
        ];
      });
    },
    [includedUsers, excludedUsers]
  );

  const teamSelectors = useMemo(
    () => <GroupsOfUsers selectionModel={selectionModel} onModelChange={setSelectionModel} />,
    [selectionModel]
  );

  const finalUsersSet = useMemo(
    () => <UsersFinalSet roleName={roleName} data={finalSetOfUsers} onSave={onSave} />,
    [roleName, finalSetOfUsers]
  );

  const usersStateManagement = useMemo(
    () => (
      <Grid container sx={{ height: '100%', overflow: 'auto' }}>
        <Grid item md={12} >
          <AddUsers data={includedUsers} onDelete={setIncludedUsers} onChange={(user) => onUpdateAssignedUsers(user)} />
        </Grid>

        <Grid item md={12} sx={{ mt:'20px'}}>
          <ExcludeUsers
            data={excludedUsers}
            onDelete={setExcludedUsers}
            onChange={(user) =>
              onUpdateAssignedUsers(user, true, loggedRoundRobinUsers, shouldFetchRoundRobinLoggedUsers, isRoundRobinLoading)
            }
          />
        </Grid>
      </Grid>
    ),
    [includedUsers, excludedUsers, loggedRoundRobinUsers, shouldFetchRoundRobinLoggedUsers, isRoundRobinLoading]
  );

  return shouldFetchRoundRobinLoggedUsers && isRoundRobinLoading ? null : (
    <Modal
      hideBackdrop
      fullWidth
      title="Role User Selection"
      resetPosition={roleModalPosition}
      open={!!roleModalPosition}
      dialogWidth="95%"
      dialogMinWidth="1100px"
      dialogMaxWidth="1500px"
      maxWidth="xl"
      onClose={handleClose}
    >
      <RoleModalLayout
        roleName={roleName}
        teamSelectorsSlot={teamSelectors}
        usersStateManagementSlot={usersStateManagement}
        finalUsersSlot={finalUsersSet}
      />
    </Modal>
  );
};

